import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Typography, Space, Rate } from 'antd';
import { useToast } from '../../contexts/ToastContext';
import { StarOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface MentorRatingApplication {
  id: number;
  name: string;
  type: number;
  mentorId: number;
  menteeId: number;
  eventDate?: string;
}

interface MentorRatingModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  application: MentorRatingApplication | null;
}

const MentorRatingModal: React.FC<MentorRatingModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  application,
}) => {
  const toast = useToast();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);

  const handleSubmit = async (values: { content: string; title?: string }) => {
    if (!application) return;

    if (rating === 0) {
      toast.error('Lütfen 1-5 arası puan verin!');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.rateMentor({
        content: values.content,
        rating,
        title: values.title || '',
        type: application.type,
        applicationId: application.id,
      });

      if (response.success) {
        toast.success('Mentör değerlendirmesi kaydedildi!');
        form.resetFields();
        setRating(0);
        onSuccess();
        onCancel();
      } else {
        toast.error(response.message || 'Değerlendirme kaydedilemedi.');
      }
    } catch (error) {
      console.error('Mentör değerlendirilirken hata:', error);
      toast.error('Mentör değerlendirilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setRating(0);
    }
  }, [visible, form]);

  return (
    <Modal
      title={<Title level={4} style={{ margin: 0 }}>Mentörü Değerlendir</Title>}
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      width={700}
    >
      <div style={{ padding: '20px 0' }}>
        {application && (
          <div style={{ marginBottom: 20, padding: 15, background: '#f0f2f5', borderRadius: 8 }}>
            <Text strong>{application.name}</Text>
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Başlık (Opsiyonel)">
            <Input placeholder="Değerlendirme başlığı..." />
          </Form.Item>

          <Form.Item label="Puanlama" required>
            <Rate
              value={rating}
              onChange={setRating}
              character={<StarOutlined />}
              style={{ fontSize: 24 }}
            />
            <Text type="secondary" style={{ marginLeft: 10 }}>
              {rating > 0 ? `${rating} yıldız` : '1-5 arası puan verin'}
            </Text>
          </Form.Item>

          <Form.Item
            name="content"
            label="Değerlendirme"
            rules={[{ required: true, message: 'Lütfen değerlendirme yazın!' }]}
          >
            <TextArea rows={6} placeholder="Mentörünüz hakkında değerlendirme yazın..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={onCancel}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={loading} disabled={rating === 0} icon={<StarOutlined />}>
                Değerlendirmeyi Kaydet
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default MentorRatingModal;
